import { bungie } from '../lib/bungie';
import { items, table, tablesOf } from '../lib/manifest';
import type { Hub, HubActivity } from './hub';

/** Port of the app's Operations: what is on in the game right now, the season pass, the Seasonal Hub's weekly track. */

export interface OpItem { name: string; icon: string; desc: string }
export interface Operation {
  kind: string; name: string; desc: string; image: string; icon?: string;
  modifiers: OpItem[]; rewards: OpItem[]; ends?: Date; featured: boolean; facts: string[];
}

const usable = (icon?: string) => !!icon && !icon.includes('missing_icon');

/** This week's milestones as Bungie reports them (the raid rotation and other weekly activities), in Bungie's order. */
export async function loadMilestones(): Promise<Operation[]> {
  const [live, t, lite] = await Promise.all([
    bungie<Record<string, any>>('/Destiny2/Milestones/'),
    tablesOf('DestinyMilestoneDefinition', 'DestinyActivityDefinition', 'DestinyActivityModifierDefinition'),
    items(),
  ]);
  const list: { order: number; op: Operation }[] = [];
  for (const [hash, m] of Object.entries(live)) {
    const first = m.activities?.[0];
    if (!first) continue;
    const milestone = t.DestinyMilestoneDefinition[hash];
    const activity = t.DestinyActivityDefinition[first.activityHash];
    const image = activity?.pgcrImage || milestone?.image || '';
    if (!image) continue;
    const mods = (first.modifierHashes ?? []).map((h: number) => t.DestinyActivityModifierDefinition[h]?.displayProperties)
      .filter((d: any) => d?.name && d.icon).map((d: any) => ({ name: d.name, icon: d.icon, desc: d.description }));
    const rewardHashes = [...new Set<number>((activity?.rewards ?? []).flatMap((r: any) => r.rewardItems ?? []).map((i: any) => i.itemHash).filter(Boolean))].slice(0, 8);
    const rewards = rewardHashes.map((h) => lite[h]).filter((d) => d?.name && d.icon).map((d) => ({ name: d.name, icon: d.icon, desc: d.desc }));
    const name = milestone?.displayProperties?.name || activity?.displayProperties?.name || '';
    if (!name) continue;
    // Bungie lists every raid here, each with its own place in the order (the newest raid first): none is "featured"
    list.push({
      order: m.order ?? 0,
      op: {
        kind: 'Raid', name, desc: activity?.displayProperties?.description || milestone?.displayProperties?.description || '', image,
        icon: usable(milestone?.displayProperties?.icon) ? milestone.displayProperties.icon : undefined,
        modifiers: mods, rewards, ends: m.endDate ? new Date(m.endDate) : undefined, featured: rewards.length > 1, facts: [],
      },
    });
  }
  return list.sort((a, b) => a.order - b.order || a.op.name.localeCompare(b.op.name)).map((x) => x.op);
}

// kinds that make a card, in the order they are shown
const CARD_KINDS = ['Crawl', 'Dungeon', 'Solo Ops', 'Nightfall', 'Vanguard Op', 'Exotic Mission', 'The Crucible', 'Onslaught', 'Gambit', 'Seasonal Arena', 'The Lawless Frontier', 'Strike'];
const VARIANT = /:\s*(Customize|Matchmade|Standard|Private|Normal|Advanced|Expert|Master|Legend|Legendary|Ultimate|Grandmaster|Brave|Fabled)$/i;

/** One card per kind of activity this character can launch now (Dungeons, Crawl, Nightfall...), each a list of pages. */
export async function availableCards(available: HubActivity[]): Promise<{ kind: string; pages: Operation[] }[]> {
  if (!available.length) return [];
  const [t, lite] = await Promise.all([tablesOf('DestinyActivityDefinition', 'DestinyActivityTypeDefinition', 'DestinyActivityModifierDefinition'), items()]);
  const defs = available.map((live) => ({ live, def: t.DestinyActivityDefinition[live.hash] })).filter((x) => x.def);
  const kindOf = (x: { def: any }) => t.DestinyActivityTypeDefinition[x.def.activityTypeHash]?.displayProperties?.name ?? '';
  const result: { kind: string; pages: Operation[] }[] = [];
  for (const kind of CARD_KINDS) {
    const list = defs.filter((x) => kindOf(x) === kind && x.def.pgcrImage);
    if (!list.length) continue;
    // the same activity comes in several variants (Matchmade, Customize, Master...): one page per activity
    const groups = new Map<string, typeof list>();
    for (const x of list) {
      const key = (x.def.displayProperties?.name ?? '').replace(VARIANT, '').trim();
      if (!key) continue;
      groups.set(key, [...(groups.get(key) ?? []), x]);
    }
    const ordered = [...groups.entries()]
      .sort((a, b) => Number(b[1].some((x) => x.live.focused)) - Number(a[1].some((x) => x.live.focused)) || a[0].localeCompare(b[0]))
      .slice(0, 8);
    const pages: Operation[] = ordered.map(([name, g]) => {
      const pick = [...g].sort((a, b) => Number(!!b.def.matchmaking?.isMatchmade) - Number(!!a.def.matchmaking?.isMatchmade) || b.live.rewards.length - a.live.rewards.length)[0];
      const mm = pick.def.matchmaking;
      const facts: string[] = [];
      if (mm) {
        facts.push(`Matchmaking: ${mm.isMatchmade ? 'On' : 'Off'}`);
        const min = mm.minParty ?? 0, max = mm.maxParty ?? 0;
        if (max > 0) facts.push(min > 0 && min !== max ? `Fireteam: ${min}-${max} Players` : `Fireteam: ${max} Player${max === 1 ? '' : 's'}`);
      }
      if (pick.live.light > 0) facts.push(`Power ${pick.live.light}`);
      const typeIcon = t.DestinyActivityTypeDefinition[pick.def.activityTypeHash]?.displayProperties?.icon;
      const actIcon = pick.def.displayProperties?.icon;
      const rewards = pick.live.rewards.map((h) => lite[h]).filter((d) => d?.name && d.icon);
      return {
        kind, name, desc: pick.def.displayProperties?.description ?? '', image: pick.def.pgcrImage,
        icon: usable(typeIcon) ? typeIcon : usable(actIcon) ? actIcon : undefined,
        modifiers: pick.live.modifiers.map((h) => t.DestinyActivityModifierDefinition[h]?.displayProperties).filter((d: any) => d?.name && d.icon).map((d: any) => ({ name: d.name, icon: d.icon, desc: d.description })),
        rewards: rewards.filter((r, i) => rewards.findIndex((x) => x.name === r.name) === i).map((d) => ({ name: d.name, icon: d.icon, desc: d.desc })),
        featured: g.some((x) => x.live.focused), facts,
      };
    });
    if (pages.length) result.push({ kind, pages });
  }
  return result;
}

// ---------------- the season and its pass ----------------

export interface SeasonReward { level: number; hash: number; name: string; icon: string; premium: boolean }
export interface SeasonInfo { name: string; passName: string; desc: string; background: string; rewardProgression: number; prestigeProgression: number; ends?: Date; rewards: SeasonReward[] }

/** The season, its pass, and the next ten ranks of rewards after the player's rank (both tracks). */
export async function loadSeason(hub: Hub, charId: string | undefined): Promise<SeasonInfo | null> {
  if (!hub.season || !hub.seasonPass) return null;
  const t = await tablesOf('DestinySeasonDefinition', 'DestinySeasonPassDefinition', 'DestinyProgressionDefinition');
  const lite = await items();
  const s = t.DestinySeasonDefinition[hub.season];
  const p = t.DestinySeasonPassDefinition[hub.seasonPass];
  if (!s || !p) return null;
  const reward = p.rewardProgressionHash ?? 0;
  const rank = hub.progress(charId, reward)?.level ?? 0;
  const track = t.DestinyProgressionDefinition[reward];
  const all = (track?.rewardItems ?? []).map((i: any) => ({ level: i.rewardedAtProgressionLevel, hash: i.itemHash, premium: i.uiDisplayStyle === 'premium' }));
  const levels = [...new Set<number>(all.map((i: any) => i.level))].sort((a, b) => a - b);
  let shown = new Set(levels.filter((l) => l > rank).slice(0, 10));
  if (!shown.size) shown = new Set(levels.slice(-10));
  const end = (s.seasonPassList ?? []).find((x: any) => x.seasonPassHash === hub.seasonPass)?.seasonPassEndDate;
  return {
    name: s.displayProperties?.name ?? '', passName: p.displayProperties?.name ?? '', desc: s.displayProperties?.description ?? '',
    background: s.backgroundImagePath ?? '', rewardProgression: reward, prestigeProgression: p.prestigeProgressionHash ?? 0,
    ends: end && new Date(end).getFullYear() < 2090 ? new Date(end) : undefined,
    rewards: all.filter((i: any) => shown.has(i.level)).map((i: any) => ({ level: i.level, hash: i.hash, premium: i.premium, name: lite[i.hash]?.name ?? '', icon: lite[i.hash]?.icon ?? '' })).filter((r: SeasonReward) => r.name),
  };
}

// ---------------- Seasonal Hub > Weekly Rewards ----------------

export interface WeeklyReward { rank: number; item: number; name: string; icon: string; quantity: number; earned: boolean; claimed: boolean }
export interface WeeklyTrack { rank: number; ranks: number; into: number; size: number; rewards: WeeklyReward[] }

export async function loadWeekly(hub: Hub, charId: string, eventCard: number): Promise<WeeklyTrack | null> {
  if (eventCard <= 1) return null;
  const [cards, progressions, lite] = await Promise.all([table<any>('DestinyEventCardDefinition'), table<any>('DestinyProgressionDefinition'), items()]);
  const trackHash = cards[eventCard]?.rewardProgressionHash;
  const live = trackHash ? hub.rawProgressions?.[charId]?.progressions?.[trackHash] : undefined;
  const def = trackHash ? progressions[trackHash] : undefined;
  if (!live || !def) return null;
  const offered: any[] = def.rewardItems ?? [];
  const states: number[] = live.rewardItemStates ?? [];
  const rewards = offered.map((r, i) => {
    const d = lite[r.itemHash];
    const state = states[i] ?? 0;   // 2 = earned, 4 = claimed
    return { rank: r.rewardedAtProgressionLevel, item: r.itemHash, name: d?.name ?? '', icon: d?.icon ?? '', quantity: Math.max(1, r.quantity ?? 1), earned: (state & 2) !== 0, claimed: (state & 4) !== 0 };
  }).sort((a, b) => a.rank - b.rank);
  return { rank: live.level ?? 0, ranks: live.levelCap ?? 0, into: live.progressToNextLevel ?? 0, size: live.nextLevelAt ?? 0, rewards };
}

// Distortions and Rotations are worked out from the clock alone, in ../lib/schedule (the server's page text uses them too)
export { distortionAt, minutesToNextDistortion, ROTATIONS, ROTATION_ANCHOR } from '../lib/schedule';
